import { useCallback, useEffect, useMemo, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Icon, type IEachItem, Table } from '@wf/components';
import { localStorageService } from '@wf/sw-ui-kit';

import TableGrid, { type ITableColumn } from '../../../../components/TableGrid';
import TableFooter from '../../../../components/TableFooter';
import PrintLicenseBtn from './PrintLicenseBtn';
import LicensesSearch from './LicensesSearch';

import useLicenseAbbreviation from '../../../../hooks/useLicenseAbbreviation';
import useLicenseTypeNature from '../../../../hooks/useLicenseTypeNature';
import { PAGINATION_LIMIT, SortDirection } from '../../../../constants';
import useUpdateAccess from '../../../../hooks/useUpdateAccess';
import { type ILicenseSearchResult } from '../../store/types';
import { type IRestriction } from '../../../../types';

import {
  setLicensesPagination,
  setLicensesSortConfig,
  setNewLicensesPagination,
  setNewLicensesSortConfig,
} from '../../store/slices';
import { getLicenseTableColumns, getLicenseTableColumnSettings, licenseTableKeys } from './tableConfigs';
import { licenseSelectFields, lpcoListStatuses, newRequestStatuses } from '../../configs';
import { licensesSelector, newLicensesSelector } from '../../store/selectors';
import { getLicenses, getNewLicenses } from '../../store/actions';
import { getRestrictionsFromLocalStorage } from './helpers';

import styles from './LicensesTable.module.scss';

interface ILicensesTable {
  tableName: string;
  isNewLicenses: boolean;
}

function LicensesTable({ tableName, isNewLicenses }: ILicensesTable) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const licenseTypeNature = useLicenseTypeNature();
  const licenseAbbreviation = useLicenseAbbreviation();
  const hasUpdateAccess = useUpdateAccess();

  const {
    data: licenseTableRows,
    totalCount,
    isLoading,
    pagination,
    sortConfig,
  } = useSelector(isNewLicenses ? newLicensesSelector(licenseTypeNature) : licensesSelector(licenseTypeNature));

  const restrictionKey = `${tableName}::RESTRICTIONS`;

  const [restrictions, setRestrictions] = useState<Record<string, IRestriction>>(
    () => getRestrictionsFromLocalStorage(restrictionKey) ?? {}
  );

  const [columnSettings, setColumnSettings] = useState<IEachItem[]>(() =>
    getLicenseTableColumnSettings(t, tableName, licenseAbbreviation)
  );

  useEffect(() => {
    setColumnSettings(getLicenseTableColumnSettings(t, tableName, licenseAbbreviation));
  }, [licenseAbbreviation, t, tableName]);

  const renderedColumns = useMemo(() => {
    const checkedColumnItemIds = columnSettings.filter((item) => item.toggleProps?.checked).map((item) => item.id);
    const columns = getLicenseTableColumns(
      tableName,
      t('Type of {{licenseAbbreviation}}', { licenseAbbreviation: t(licenseAbbreviation) })
    );

    return checkedColumnItemIds
      .map((id) => columns[id])
      .filter((item) => !!item)
      .concat({
        key: licenseTableKeys.id,
        name: '',
        colRenderer: (id: number, _, { isPrintable }) => (
          <div className={styles.actionsCellContent}>
            {isPrintable && <PrintLicenseBtn id={`${id}`} />}
            <Icon
              onClick={(e) => {
                e.stopPropagation();
                navigate(`view/${id}`);
              }}
              name="ic_eye"
            />
          </div>
        ),
        className: styles.actionsCell,
      }) as ITableColumn<ILicenseSearchResult>[];
  }, [columnSettings, licenseAbbreviation, navigate, t, tableName]);

  const handleSaveColumnSettings = (settings: IEachItem[]) => {
    setColumnSettings(settings);
    localStorageService.set(tableName, settings);
  };

  const onRowClick = (row: ILicenseSearchResult) => {
    navigate(`${hasUpdateAccess ? 'edit' : 'view'}/${row.id}`);
  };

  const getLicensesData = useCallback(() => {
    const payload = {
      page: pagination.currentPage,
      selectFields: licenseSelectFields,
      sortByFields: {
        field: sortConfig.field,
        mode: sortConfig.mode,
      },
      excludedStatuses: isNewLicenses ? lpcoListStatuses : newRequestStatuses,
      restrictions,
      licenseTypeNature,
    };

    dispatch(isNewLicenses ? getNewLicenses(payload) : getLicenses(payload));
  }, [
    pagination.currentPage,
    sortConfig.field,
    sortConfig.mode,
    isNewLicenses,
    restrictions,
    licenseTypeNature,
    dispatch,
  ]);

  const handleSort = (sortKey: string, isAsc: boolean) => {
    const sortConfig = {
      field: sortKey as keyof ILicenseSearchResult,
      mode: isAsc ? SortDirection.ASC : SortDirection.DESC,
    };
    dispatch(
      isNewLicenses
        ? setNewLicensesSortConfig({ sortConfig, licenseTypeNature })
        : setLicensesSortConfig({ sortConfig, licenseTypeNature })
    );
  };

  const handlePageChange = (newPage: number) => {
    dispatch(
      isNewLicenses
        ? setNewLicensesPagination({ currentPage: newPage, licenseTypeNature })
        : setLicensesPagination({ currentPage: newPage, licenseTypeNature })
    );
  };

  const changeRestrictions = (newRestrictions: Record<string, IRestriction>) => {
    handlePageChange(1);
    setRestrictions(newRestrictions);
    localStorageService.set(restrictionKey, newRestrictions);
  };

  useEffect(() => {
    getLicensesData();
  }, [getLicensesData]);

  return (
    <Table className={styles.table}>
      <LicensesSearch
        saveColumnSettings={handleSaveColumnSettings}
        setRestrictions={changeRestrictions}
        columnSettings={columnSettings}
        isNewLicenses={isNewLicenses}
        restrictions={restrictions}
        tableName={tableName}
      />

      <TableGrid
        noResultTitle={t('No {{licenseAbbreviation}} list data yet', {
          licenseAbbreviation: t(licenseAbbreviation),
        })}
        isAsc={sortConfig.mode === SortDirection.ASC}
        isSortedBy={sortConfig.field}
        columns={renderedColumns}
        onRowClick={onRowClick}
        rows={licenseTableRows}
        handleSort={handleSort}
        isLoading={isLoading}
        withScrolling
      />

      <TableFooter
        handlePageChange={handlePageChange}
        page={pagination.currentPage}
        pageSize={PAGINATION_LIMIT}
        totalCount={totalCount}
      />
    </Table>
  );
}

export default LicensesTable;
